import React from "react";
import { COLORS } from "../utils/constants";
import { LanguageCode, pickLang } from "../utils/translations";

const ENABLE_EXPERIMENTAL_MAP = true; // set false to disable experiment

interface SocialHeroProps {
  title: {
    main: string;
    highlight: string;
  };
  subtitle: string;
  ctaText: string;
  lang?: LanguageCode;
}

const HERO_LABELS: Partial<
  Record<
    LanguageCode,
    { youtubeContent: string; mainDashboard: string; facebook: string; instaGrowth: string }
  >
> = {
  en: {
    youtubeContent: "YouTube Content",
    mainDashboard: "Main Dashboard",
    facebook: "Facebook",
    instaGrowth: "Insta Growth",
  },
  hi: {
    youtubeContent: "YouTube कंटेंट",
    mainDashboard: "मुख्य डैशबोर्ड",
    facebook: "Facebook",
    instaGrowth: "इंस्टा ग्रोथ",
  },
  fr: {
    youtubeContent: "Contenu YouTube",
    mainDashboard: "Tableau de bord principal",
    facebook: "Facebook",
    instaGrowth: "Croissance Insta",
  },
};

const Hero: React.FC<SocialHeroProps> = ({ title, subtitle, ctaText, lang = "en" }) => {

  //const brandPurple = "#714B67";
  //const brandTeal = "#017E84";

  const formatSubtitle = (text: string) => {
    const parts = text.split(/({YT}|{WA}|{EM})/g);

    return parts.map((part, i) => {
      if (part === "{YT}")
        return (
          <strong key={i} style={{ color: "#1A202C" }}>
            YouTube
          </strong>
        );
      if (part === "{WA}")
        return (
          <strong key={i} style={{ color: "#1A202C" }}>
            WhatsApp
          </strong>
        );
      if (part === "{EM}")
        return (
          <strong key={i} style={{ color: "#1A202C" }}>
            Email
          </strong>
        );
      return part;
    });
  };

  return (
    <section
      id="social-hero"
      style={{
        padding: "24px 6%",
        background: "linear-gradient(to bottom, #ffffff, #f9f9ff)",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {ENABLE_EXPERIMENTAL_MAP && <ExperimentalMapLayer labels={pickLang(HERO_LABELS, lang)} />}

      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <h1
          style={{
            fontSize: "3.8rem",
            color: COLORS.brandPurple,
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: "1rem",
            marginTop: "0",
          }}
        >
          {title.main}
          <br />
          <span style={{ color: COLORS.brandTeal }}>{title.highlight}</span>
        </h1>

        <p
          style={{
            fontSize: "1.2rem",
            color: "#4A5568",
            maxWidth: "800px",
            margin: "0 auto 1.5rem",
            lineHeight: 1.5,
          }}
        >
          {formatSubtitle(subtitle)}
        </p>

        <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
          <button
            style={{
              backgroundColor: COLORS.brandPurple,
              color: "white",
              padding: "14px 40px",
              borderRadius: "8px",
              border: "none",
              fontWeight: 700,
              fontSize: "1.1rem",
              cursor: "pointer",
              lineHeight: "1", // Ensures text is perfectly centered vertically
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "52px",
            }}
          >
            {ctaText}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;

/* ============================
   EXPERIMENTAL VISUAL LAYER
   (Fully isolated, no coupling)
   ============================ */
const ExperimentalMapLayer: React.FC<{
  labels: { youtubeContent: string; mainDashboard: string; facebook: string; instaGrowth: string };
}> = ({ labels }) => {
  //const brandPurple = "#714B67";
  //const brandTeal = "#017E84";

  const boxStyle: React.CSSProperties = {
    position: "absolute",
    backgroundColor: "#fff",
    padding: "6px 14px",
    borderRadius: "6px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    border: "1px solid #e2e8f0",
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#4A5568",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    zIndex: 3,
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          maxWidth: "1100px",
          height: "400px",
        }}
      >
        <svg width="100%" height="100%" viewBox="0 0 1000 400">
          <path
            d="M160 200 L 450 200"
            stroke={COLORS.brandTeal}
            strokeWidth="1.5"
            strokeDasharray="5 5"
            opacity="0.3"
          />
          <path
            d="M550 180 C 650 150 750 110 840 100"
            stroke={COLORS.brandPurple}
            strokeWidth="1.5"
            strokeDasharray="5 5"
            opacity="0.3"
          />
          <path
            d="M550 220 C 650 250 750 290 840 300"
            stroke={COLORS.brandPurple}
            strokeWidth="1.5"
            strokeDasharray="5 5"
            opacity="0.3"
          />

          <circle
            cx="120"
            cy="200"
            r="32"
            fill="#FFF5F5"
            stroke="#FF0000"
            strokeWidth="2"
          />
          <circle
            cx="500"
            cy="200"
            r="42"
            fill="#fff"
            stroke={COLORS.brandPurple}
            strokeWidth="3"
          />
          <circle
            cx="880"
            cy="100"
            r="28"
            fill="#F0F7FF"
            stroke="#1877F2"
            strokeWidth="2"
          />
          <circle
            cx="880"
            cy="300"
            r="28"
            fill="#FFF0F5"
            stroke="#E4405F"
            strokeWidth="2"
          />
        </svg>

        <div style={{ ...boxStyle, top: "260px", left: "80px" }}>
          <span style={{ color: "#FF0000" }}>●</span> {labels.youtubeContent}
        </div>

        <div
          style={{
            ...boxStyle,
            top: "20px",
            left: "445px",
            border: `1.5px solid ${COLORS.brandTeal}`,
          }}
        >
          <span style={{ color: COLORS.brandTeal }}>★</span> {labels.mainDashboard}
        </div>

        <div style={{ ...boxStyle, top: "35px", right: "70px" }}>
          <span style={{ color: "#1877F2" }}>f</span> {labels.facebook}
        </div>

        <div style={{ ...boxStyle, bottom: "35px", right: "70px" }}>
          <span style={{ color: "#E4405F" }}>📸</span> {labels.instaGrowth}
        </div>
      </div>
    </div>
  );
};
